import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContentText,
  Divider,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Dispatch } from '@reduxjs/toolkit';
import { TableHeader } from 'components/TranslationProgress/TranslationProgress.styled';
import { ButtonVariantEnum } from 'enums/buttonVariant';
import { OrderEnum } from 'enums/orderEnum';
import React, { Component, Fragment } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { DialogOpenPayloadJobs } from 'store/dialog/dialog.interface';
import {
  contentChangedIgnore,
  contentChangedSend,
  contentChangedUpdate,
  getProjectChangedContent,
} from 'store/projects/projects.actions';
import { getProjectChangedProductsSelector } from 'store/projects/projects.selectors';
import {
  getAuthSpinnerSelector,
  getJobActionSpinnerSelector,
  getSecondJobActionSpinnerSelector,
  getThirdJobActionSpinnerSelector,
} from 'store/spinner/spinner.selectors';
import { ChangedKontentRequest, ChangedProductsDTO } from 'utils/restApplicationClient';
import { CustomDialogButtonContainer, CustomDialogContent, CustomDialogTableCell } from '../CustomDialog.styled';

interface IProps {
  onClose: ((response: ButtonVariantEnum) => void) | undefined;
  jobs: DialogOpenPayloadJobs;
  projectId: string;
  projectLanguages: Array<string>;
}

interface IDispatchProps {
  contentChangedIgnore: (payload: ChangedKontentRequest) => AppDispatch;
  contentChangedUpdate: (payload: ChangedKontentRequest) => AppDispatch;
  contentChangedSend: (payload: ChangedKontentRequest) => AppDispatch;
  getProjectChangedContent: (payload: { projectId: string; sort: boolean }) => AppDispatch;
}

interface IStateProps {
  jobActionSpinner: boolean;
  secondJobActionSpinner: boolean;
  thirdJobActionSpinner: boolean;
  fetchSpinner: boolean;
  changedProducts: Array<ChangedProductsDTO>;
}

interface IState {
  checkboxes: Array<boolean>;
  direction: OrderEnum;
}

type PropType = IProps & WithTranslation & IDispatchProps & IStateProps;

class ContentChangedProjectCms extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);

    this.state = {
      checkboxes: [],
      direction: OrderEnum.ASC,
    };
  }

  componentDidMount(): void {
    const { getProjectChangedContent, projectId } = this.props;
    const { direction } = this.state;
    const isAsc = direction === OrderEnum.ASC;

    getProjectChangedContent({ projectId, sort: isAsc });
  }

  componentDidUpdate({ changedProducts: previousChangedProducts }: PropType): void {
    const { changedProducts } = this.props;

    if (changedProducts !== previousChangedProducts) {
      this.setState({ checkboxes: new Array(changedProducts.length).fill(false) });
    }
  }

  handleUpdateClick = (): void => {
    const { contentChangedUpdate, projectId, projectLanguages } = this.props;
    const productIds = this.getCheckedProductIds();

    contentChangedUpdate({
      productIds,
      requestId: projectId,
      kontentLanguagesCodes: projectLanguages,
      operation: 'UPDATE',
    });
  };

  handleSendClick = (): void => {
    const { contentChangedSend, projectId, projectLanguages } = this.props;
    const productIds = this.getCheckedProductIds();

    contentChangedSend({
      productIds,
      requestId: projectId,
      kontentLanguagesCodes: projectLanguages,
      operation: 'CREATE_NEW',
    });
  };

  handleIgnoreClick = (): void => {
    const { contentChangedIgnore, projectId } = this.props;
    const productIds = this.getCheckedProductIds();

    contentChangedIgnore({ productIds, requestId: projectId, operation: 'IGNORE' });
  };

  handleCheckboxChange = (index: number) => (): void => {
    const { checkboxes } = this.state;
    checkboxes[index] = !checkboxes[index];

    this.setState({ checkboxes });
  };

  handleToggleAllCheckboxes = (checked: boolean | undefined, indeterminate: boolean | undefined) => (): void => {
    const { checkboxes } = this.state;
    let temporary = [...checkboxes];
    if (checked && !indeterminate) {
      temporary = temporary.fill(false);
    } else {
      temporary = temporary.fill(true);
    }
    this.setState({
      checkboxes: temporary,
    });
  };

  handleDirectionChange = (): void => {
    const { getProjectChangedContent, projectId } = this.props;
    const { direction, checkboxes } = this.state;
    const isAsc = direction === OrderEnum.ASC;

    this.setState({
      direction: direction === OrderEnum.ASC ? OrderEnum.DESC : OrderEnum.ASC,
      checkboxes: [...checkboxes].fill(false),
    });

    getProjectChangedContent({ projectId, sort: !isAsc });
  };

  getCheckedProductIds = (): Array<string> => {
    const { changedProducts } = this.props;
    const { checkboxes } = this.state;

    const productIds: Array<string> = [];
    checkboxes.forEach((value, index) => {
      if (value) {
        productIds.push(changedProducts[index].productId);
      }
    });
    return productIds;
  };

  getHeaderCheckboxValue = (): { checked?: boolean; indeterminate?: boolean } => {
    const { checkboxes } = this.state;

    if (checkboxes.every((value) => value)) {
      return { checked: true };
    } else if (checkboxes.includes(true)) {
      return { indeterminate: true, checked: true };
    } else {
      return { checked: false };
    }
  };

  render(): JSX.Element {
    const {
      jobActionSpinner,
      secondJobActionSpinner,
      thirdJobActionSpinner,
      t,
      fetchSpinner,
      changedProducts,
    } = this.props;
    const { checkboxes, direction } = this.state;
    const { checked, indeterminate } = this.getHeaderCheckboxValue();
    const isDisabled =
      jobActionSpinner || secondJobActionSpinner || thirdJobActionSpinner || this.getCheckedProductIds().length === 0;

    return (
      <Fragment>
        <CustomDialogContent>
          <DialogContentText>
            <Typography component="span" color="textPrimary">
              {t('modal.contentChangedProjectAction1')}
            </Typography>
          </DialogContentText>
          <Box display="flex" maxHeight="260px" overflow="auto">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <CustomDialogTableCell header="true" width={25}>
                    <TableHeader>
                      <Checkbox
                        disableRipple
                        onClick={this.handleToggleAllCheckboxes(checked, indeterminate)}
                        checked={checked}
                        indeterminate={indeterminate}
                      />
                    </TableHeader>
                  </CustomDialogTableCell>
                  <CustomDialogTableCell header="true">
                    <TableHeader sortable active sortDirection={direction} onClick={this.handleDirectionChange}>
                      <Typography component="div" variant="body2" color="textPrimary">
                        <Box fontWeight="fontWeightBold">Content name</Box>
                      </Typography>
                    </TableHeader>
                  </CustomDialogTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fetchSpinner ? (
                  <TableRow>
                    <CustomDialogTableCell colSpan={2}>
                      <Box minHeight="60px" justifyContent="center" alignItems="center" display="flex">
                        <CircularProgress color="secondary" />
                      </Box>
                    </CustomDialogTableCell>
                  </TableRow>
                ) : (
                  changedProducts.map(({ name }, index) => {
                    return (
                      <TableRow key={index}>
                        <CustomDialogTableCell width={25}>
                          <Checkbox checked={checkboxes[index]} onClick={this.handleCheckboxChange(index)} />
                        </CustomDialogTableCell>
                        <CustomDialogTableCell onClick={this.handleCheckboxChange(index)}>{name}</CustomDialogTableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </CustomDialogContent>
        <Divider />
        <DialogActions>
          <CustomDialogButtonContainer>
            <Button color="secondary" disabled={isDisabled} variant="contained" onClick={this.handleUpdateClick}>
              {(jobActionSpinner && <CircularProgress size="24px" color="secondary" />) || t('modal.updateExisting')}
            </Button>
            <Button variant="outlined" disabled={isDisabled} onClick={this.handleSendClick}>
              {(secondJobActionSpinner && <CircularProgress size="24px" />) || t('modal.sendAsNew')}
            </Button>
            <Button variant="outlined" disabled={isDisabled} onClick={this.handleIgnoreClick}>
              {(thirdJobActionSpinner && <CircularProgress size="24px" />) || t('modal.ignoreUpdates')}
            </Button>
          </CustomDialogButtonContainer>
        </DialogActions>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  jobActionSpinner: getJobActionSpinnerSelector(state),
  secondJobActionSpinner: getSecondJobActionSpinnerSelector(state),
  thirdJobActionSpinner: getThirdJobActionSpinnerSelector(state),
  fetchSpinner: getAuthSpinnerSelector(state),
  changedProducts: getProjectChangedProductsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  contentChangedIgnore: (payload: ChangedKontentRequest): AppDispatch => dispatch(contentChangedIgnore(payload)),
  contentChangedSend: (payload: ChangedKontentRequest): AppDispatch => dispatch(contentChangedSend(payload)),
  contentChangedUpdate: (payload: ChangedKontentRequest): AppDispatch => dispatch(contentChangedUpdate(payload)),
  getProjectChangedContent: (payload: { projectId: string; sort: boolean }): AppDispatch =>
    dispatch(getProjectChangedContent(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ContentChangedProjectCms));
